import React, { useState, useCallback, useRef, useEffect } from 'react';
import Files from 'react-files';
import { object as objectYup, mixed as mixedYup } from 'yup';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { ReactComponent as IconTrash } from '../../../assets/icons/trash.svg';
import Swal from 'sweetalert2';
import excelIcon from '../../../assets/img/excel-icon.svg';
import { SortTypeEnum } from '../../../types';
import { ExcelApiFormDataType, ExcelResponseType } from '../../../types/excelUpload';
import { ReactFilesError, ReactFilesFile } from '../../../types/files';
import useInitialErrors from '../../../hooks/formik-initial-errors';
import { excelUploadDataService } from '../../../lib/api/excelUpload';
import { CompanyProfileType } from '../../../types/company';
import DataAsExcel from '../../../components/download/dataAsExcel';
import DownloadExcelResponse from '../../../components/excel-invites/modal/downloadExcelResponse';
import RoundTimer from '../../../components/round-timer';
import imgNoResult from '../../../assets/icons/offer-no-result.svg';
import { useConditionalEffects } from '../../../hooks/force-signout';
import { sessionStorageSetItem } from '../../../components/storage/sessionStorageManager';
import useUser from '../../../hooks/useSelector';
import { ErrorIcon, Message, SuccessIcon, SweetAlertTitle, WarningIcon } from '../../../lib/constants/messages';
import * as ExcelJS from 'exceljs';

type Props = {
    setShowCandidateUploadContent: (key: boolean) => void;
    employerCompanyDomain: string | undefined | null;
    isExistingCandidate: boolean;
};

const initialValues = {
    excelDataFileUrl: null,
    companyId: '',
};

interface SearchQueries {
    [key: string]: string;
}

const CandidateExcelBulkUpload: React.FC<Props> = ({
    setShowCandidateUploadContent,
    employerCompanyDomain,
    isExistingCandidate,
}) => {
    const [dummyData, setDummyData] = useState<any[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const [headerContent, setHeaderContent] = useState<any[][]>([]);
    const [selectedHeaders, setSelectedHeaders] = useState<string[]>(['', '', '']);
    const [selectedContent, setSelectedContent] = useState<any[][]>([]);
    const [disableSelectOption, setDisableSelectOption] = useState(true);
    const [showDataPreview, setShowDataPreview] = useState(false);
    const [resetInputField, setResetInputField] = useState(0);
    const [excelDataAsBase64String, setExcelDataAsBase64String] = useState<string>('');
    const [excelData, setExcelData] = useState<{ file?: File; error?: string } | null>(null);
    const [openProcessedDataModal, setOpenProcessedDataModal] = useState(false);
    const [isLead, setIsLead] = useState(false);
    const [allCompanyList, setAllCompanyList] = useState<CompanyProfileType[]>([]);
    // const [start, setStart] = useState(1);
    // const [limit, setLimit] = useState(allCompanyList.length);
    // const [filters, setFilters] = useState<{ [key: string]: string } | null>(null);
    const [sort, setSort] = useState<{ [key: string]: SortTypeEnum } | null>(null);
    const [disableBtn, setDisableBtn] = useState(false);
    const [passRecords, setPassRecords] = useState(0);
    const [failedRecords, setFailedRecords] = useState(0);
    const [duplicateRecords, setDuplicateRecords] = useState(0);
    const [totalOutPutData, setTotalOutPutData] = useState<ExcelResponseType[]>([]);
    const [selectedOptionLabels, setSelectedOptionLabels] = useState<string[]>(['']);
    const [excelDataHeadersLabel, setExcelDataHeadersLabel] = useState<any[]>([]);
    const [sortingOrder, setSortingOrder] = useState<{ [key: string]: string }>({});

    const hasUser = useUser();
    const signOutServiceComponent = useConditionalEffects();

    const [searchQueries, setSearchQueries] = useState<SearchQueries>({});
    const [filteredData, setFilteredData] = useState<any[][]>([]);

    const updateSearchQuery = (columnName: string, query: string) => {
        const updatedQueries = { ...searchQueries, [columnName]: query };
        setSearchQueries(updatedQueries);

        let newFilteredData = [...selectedContent];

        excelDataHeadersLabel.forEach((header, index) => {
            const searchQuery = updatedQueries[header]?.toLowerCase();

            if (searchQuery) {
                newFilteredData = newFilteredData.filter((row) => {
                    const cellValue = (row[index] || '').toString().toLowerCase();
                    return cellValue.includes(searchQuery);
                });
            }
        });

        if (Object.values(updatedQueries).every((query) => query === '')) {
            setFilteredData([...selectedContent]);
        } else {
            setFilteredData(newFilteredData);
        }
    };

    const filterData = () => {
        if (Object.values(searchQueries).every((query) => query === '')) {
            setFilteredData([...selectedContent]);
        } else {
            const newFilteredData = selectedContent.filter((row) => {
                return excelDataHeadersLabel.every((header, index) => {
                    const query = searchQueries[header] || '';
                    const cellValue = row[index];
                    if (cellValue !== null && cellValue !== undefined) {
                        return cellValue.toString().toLowerCase().includes(query.toLowerCase());
                    }
                    return false; // Handle undefined or null values
                });
            });

            setFilteredData(newFilteredData);
        }
    };

    const handleSorting = (columnName: string) => {
        const order = sortingOrder[columnName] === 'asc' ? 'desc' : 'asc';
        setSortingOrder({ [columnName]: order });

        const sortedData = [...filteredData].sort((a, b) => {
            const index = excelDataHeadersLabel.indexOf(columnName);
            const valueA = a[index] || ''; // Use an empty string as a default
            const valueB = b[index] || ''; // Use an empty string as a default

            if (order === 'asc') {
                return valueA.localeCompare(valueB);
            } else {
                return valueB.localeCompare(valueA);
            }
        });

        setFilteredData(sortedData);
    };

    const gmailDomain = 'gmail.com';

    const areAllHeadersPresent = (cell: string): boolean => {
        const sanitizedRow = typeof cell === 'string' ? cell.replace(/\s/g, '').toLowerCase() : cell;
        const sanitizedHeaders = excelDataHeadersLabel.map((header) => header.replace(/\s/g, '').toLowerCase());
        return sanitizedHeaders.includes(sanitizedRow);
    };

    const handleFileInputChange = (files: File[]) => {
        const file = files[0];
        if (!file) {
            Swal.fire({
                title: SweetAlertTitle.Sorry,
                icon: WarningIcon,
                text: Message.NoFileSelected,
            });
            return;
        }

        if (!file.name.endsWith('.xlsx')) {
            Swal.fire({
                title: SweetAlertTitle.Sorry,
                icon: WarningIcon,
                text: 'Only .xlsx files are supported.',
            });
            return;
        }

        if (files[0]) {
            const file = files[0];
            setDisableSelectOption(true);
            let isAnyError = false;

            const reader = new FileReader();
            reader.onload = async (e) => {
                const data = e.target?.result;
                if (data) {
                    // const workbook = XLSX.read(data, { type: 'binary' });
                    // const sheetName = workbook.SheetNames[0];
                    // const sheet = workbook.Sheets[sheetName];
                    // const sheetData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
                    const workbook = new ExcelJS.Workbook();
                    await workbook.xlsx.load(data as ArrayBuffer);
                    const sheet = workbook.worksheets[0];
                    const rawSheetData = sheet.getSheetValues().slice(1) as (any[] | undefined)[];
                    const sheetData: any[][] = rawSheetData
                        .filter((row): row is any[] => Array.isArray(row) && row.some((cell) => cell))
                        .map((row) =>
                            row.map((cell) => {
                                if (typeof cell === 'object' && cell !== null) {
                                    return cell.text || JSON.stringify(cell);
                                }
                                return String(cell ?? '').trim();
                            })
                        );

                    let cleanedSheetData = sheetData;
                    if (sheetData.length > 0) {
                        cleanedSheetData = sheetData.map((row) => row.slice(1));
                    }

                    // const nonEmptyRows = sheetData.filter((row) => row.some((cell) => cell !== null && cell !== ''));
                    const nonEmptyRows = cleanedSheetData;
                    let shouldContinue = true;
                    let headersPresent = false;

                    if (nonEmptyRows.length !== 0) {
                        if (nonEmptyRows.length > 51) {
                            Swal.fire({
                                title: SweetAlertTitle.Sorry,
                                icon: WarningIcon,
                                text: Message.ExcelRecordLimitExceed,
                            });
                            shouldContinue = false;
                            isAnyError = true;
                            setExcelData(null);
                            resetState();
                            setResetInputField(resetInputField + 1);
                            return;
                        }

                        const limitedRows = nonEmptyRows.slice(0, 51);

                        for (const row of limitedRows[0]) {
                            headersPresent = areAllHeadersPresent(row);
                            if (!headersPresent) {
                                shouldContinue = false;
                                isAnyError = true;
                                setShowDataPreview(false);
                                setExcelData(null);
                                resetState();
                                setResetInputField(resetInputField + 1);
                                Swal.fire({
                                    title: SweetAlertTitle.Sorry,
                                    icon: WarningIcon,
                                    text: Message.ExcelHeaderMismatch,
                                });
                                return;
                            }
                        }
                        if (limitedRows.length > 1) {
                            const extractedHeaders = limitedRows[0].map((header: any) => String(header));
                            const headerContent = limitedRows.slice(1);
                            headerContent.map((column) => {
                                if (!shouldContinue) return;
                                const isValid = isValidEmail(column[0]);
                                if (!isValid) {
                                    isAnyError = true;
                                    setShowDataPreview(false);
                                    setExcelData(null);
                                    resetState();
                                    setResetInputField((prev) => prev + 1);
                                    if (column[1] && column[3]) {
                                        Swal.fire({
                                            icon: WarningIcon,
                                            text: 'Please enter a valid email for ' + column[1] + ' ' + column[3],
                                        });
                                    } else {
                                        Swal.fire({
                                            icon: WarningIcon,
                                            text: Message.InvalidEmail,
                                        });
                                    }
                                    shouldContinue = false;
                                } else if (typeof column[0] === 'string') {
                                    const atIndex = column[0]?.indexOf('@');
                                    const uploadedCompanyDomainValue =
                                        atIndex !== -1 && atIndex !== undefined
                                            ? column[0]?.substring(atIndex + 1)
                                            : null;
                                    if (
                                        uploadedCompanyDomainValue == employerCompanyDomain &&
                                        uploadedCompanyDomainValue !== gmailDomain
                                    ) {
                                        isAnyError = true;
                                        setShowDataPreview(false);
                                        setExcelData(null);
                                        resetState();
                                        setResetInputField((prev) => prev + 1);
                                        if (column[1] && column[3]) {
                                            Swal.fire({
                                                icon: WarningIcon,
                                                text:
                                                    'Kindly refrain from sending invites to the work email address. Instead, we kindly ask that you send them to personal email for \n ' +
                                                    column[1] +
                                                    ' ' +
                                                    column[3],
                                            });
                                        } else {
                                            Swal.fire({
                                                icon: WarningIcon,
                                                text: Message.EmailMatchesWithCompanyDomain,
                                            });
                                        }
                                        shouldContinue = false;
                                    }
                                }
                            });

                            const objectKeys = Object.keys(dummyData[0]).map((key) => key.toLowerCase());
                            const allNamesPresent = extractedHeaders.every((name) =>
                                objectKeys.includes(name.toLowerCase().trim())
                            );
                            if (allNamesPresent && extractedHeaders.length === objectKeys.length) {
                                setHeaders(extractedHeaders);
                                setExcelData({
                                    file,
                                });
                                setHeaderContent(headerContent);
                                // setFilteredData(headerContent);
                                setDisableSelectOption(false);
                            } else {
                                isAnyError = true;
                                shouldContinue = false;
                                setExcelData(null);
                                resetState();
                                setResetInputField(resetInputField + 1);
                                setShowDataPreview(false);
                                Swal.fire({
                                    title: SweetAlertTitle.Sorry,
                                    icon: WarningIcon,
                                    text: Message.ExcelHeaderMismatch,
                                });
                            }

                            const initialSelectedHeaders = excelDataHeadersLabel.map(
                                (label) =>
                                    extractedHeaders.find((header) => {
                                        if (header) {
                                            return (
                                                header.replaceAll(' ', '').toLowerCase() ===
                                                label.replaceAll(' ', '').toLowerCase()
                                            );
                                        }
                                    }) || ''
                            );
                            setSelectedHeaders(initialSelectedHeaders);
                            setSelectedOptionLabels(initialSelectedHeaders);

                            const columnIndexMap: { [key: string]: number } = {};
                            extractedHeaders.forEach((header, columnIndex) => {
                                columnIndexMap[header] = columnIndex;
                            });
                            const selectedContent = headerContent.map((row) =>
                                initialSelectedHeaders.map((header) => row[columnIndexMap[header]])
                            );
                            setSelectedContent(selectedContent);
                        } else {
                            isAnyError = true;
                            shouldContinue = false;
                            setExcelData(null);
                            resetState();
                            setResetInputField(resetInputField + 1);
                            setShowDataPreview(false);
                            Swal.fire({
                                title: SweetAlertTitle.Sorry,
                                icon: WarningIcon,
                                text: Message.HeadersWithNoData,
                            });
                        }
                    } else {
                        isAnyError = true;
                        shouldContinue = false;
                        setExcelData(null);
                        resetState();
                        setResetInputField(resetInputField + 1);
                        Swal.fire({
                            title: SweetAlertTitle.Sorry,
                            icon: WarningIcon,
                            text: Message.EmptyExcelUploaded,
                        });
                    }
                } else {
                    isAnyError = true;
                    setExcelData(null);
                    resetState();
                    setResetInputField(resetInputField + 1);
                    Swal.fire({
                        title: SweetAlertTitle.Sorry,
                        icon: WarningIcon,
                        text: Message.UnableToReadExcel,
                    });
                }
            };
            // reader.readAsBinaryString(file);
            reader.readAsArrayBuffer(file);
            reader.onloadend = () => {
                isAnyError ? setShowDataPreview(false) : setShowDataPreview(true);
            };
        } else {
            setExcelData(null);
            resetState();
            setResetInputField(resetInputField + 1);
            Swal.fire({
                title: SweetAlertTitle.Sorry,
                icon: WarningIcon,
                text: Message.NoFileSelected,
            });
        }
    };

    // const handleFileInputChange = (files: File[]) => {
    //     try {
    //         const file = files[0];
    //         if (!file) {
    //             Swal.fire({
    //                 title: SweetAlertTitle.Sorry,
    //                 icon: WarningIcon,
    //                 text: Message.NoFileSelected,
    //             });
    //             return;
    //         }

    //         // Check file extension
    //         if (!file.name.endsWith('.xlsx')) {
    //             Swal.fire({
    //                 title: SweetAlertTitle.Sorry,
    //                 icon: WarningIcon,
    //                 text: 'Only .xlsx files are supported.',
    //             });
    //             return;
    //         }

    //         setDisableSelectOption(true);
    //         const reader = new FileReader();

    //         reader.onload = async (e) => {
    //             try {
    //                 const buffer = e.target?.result;
    //                 if (!buffer) throw new Error('Unable to read file data');

    //                 const workbook = new ExcelJS.Workbook();
    //                 await workbook.xlsx.load(buffer as ArrayBuffer);

    //                 const sheet = workbook.worksheets[0];
    //                 const rawSheetData = sheet.getSheetValues().slice(1) as (any[] | undefined)[];
    //                 const sheetData: any[][] = rawSheetData
    //                     .filter((row): row is any[] => Array.isArray(row) && row.some((cell) => cell))
    //                     .map((row) =>
    //                         row.map((cell) => {
    //                             if (typeof cell === 'object' && cell !== null) {
    //                                 return cell.text || JSON.stringify(cell);
    //                             }
    //                             return String(cell ?? '').trim();
    //                         })
    //                     );

    //                 const cleanedSheetData = sheetData.map((row) => row.slice(1));
    //                 const nonEmptyRows = cleanedSheetData;

    //                 if (nonEmptyRows.length === 0) {
    //                     throw new Error(Message.EmptyExcelUploaded);
    //                 }

    //                 if (nonEmptyRows.length > 51) {
    //                     throw new Error(Message.ExcelRecordLimitExceed);
    //                 }

    //                 const limitedRows = nonEmptyRows.slice(0, 51);
    //                 const headerRow = limitedRows[0];
    //                 const headersPresent = areAllHeadersPresent(headerRow as any);

    //                 if (!headersPresent) {
    //                     throw new Error(Message.ExcelHeaderMismatch);
    //                 }

    //                 const extractedHeaders = headerRow.map((header) => String(header));
    //                 const headerContent = limitedRows.slice(1);

    //                 for (const column of headerContent) {
    //                     const isValid = isValidEmail(column[0]);
    //                     if (!isValid) {
    //                         throw new Error(
    //                             column[1] && column[3]
    //                                 ? `Please enter a valid email for ${column[1]} ${column[3]}`
    //                                 : Message.InvalidEmail
    //                         );
    //                     }

    //                     const email = column[0] as string;
    //                     const domain = email.split('@')[1];
    //                     if (domain === employerCompanyDomain && domain !== gmailDomain) {
    //                         throw new Error(
    //                             column[1] && column[3]
    //                                 ? `Kindly refrain from sending invites to the work email address. Instead, send to personal email for ${column[1]} ${column[3]}`
    //                                 : Message.EmailMatchesWithCompanyDomain
    //                         );
    //                     }
    //                 }

    //                 const objectKeys = Object.keys(dummyData[0]).map((key) => key.toLowerCase());
    //                 const allHeadersValid = extractedHeaders.every((name) =>
    //                     objectKeys.includes(name.toLowerCase().trim())
    //                 );

    //                 if (!allHeadersValid || extractedHeaders.length !== objectKeys.length) {
    //                     throw new Error(Message.ExcelHeaderMismatch);
    //                 }

    //                 setHeaders(extractedHeaders);
    //                 setExcelData({ file });
    //                 setHeaderContent(headerContent);

    //                 const initialSelectedHeaders = excelDataHeadersLabel.map(
    //                     (label) =>
    //                         extractedHeaders.find(
    //                             (header) =>
    //                                 header.replaceAll(' ', '').toLowerCase() === label.replaceAll(' ', '').toLowerCase()
    //                         ) || ''
    //                 );

    //                 setSelectedHeaders(initialSelectedHeaders);
    //                 setSelectedOptionLabels(initialSelectedHeaders);

    //                 const columnIndexMap: { [key: string]: number } = {};
    //                 extractedHeaders.forEach((header, i) => {
    //                     columnIndexMap[header] = i;
    //                 });

    //                 const selectedContent = headerContent.map((row) =>
    //                     initialSelectedHeaders.map((header) => row[columnIndexMap[header]])
    //                 );

    //                 setSelectedContent(selectedContent);
    //                 setShowDataPreview(true);
    //                 setDisableSelectOption(false);
    //             } catch (err: any) {
    //                 console.error(err);
    //                 setExcelData(null);
    //                 resetState();
    //                 setResetInputField((prev) => prev + 1);
    //                 setShowDataPreview(false);
    //                 Swal.fire({
    //                     title: SweetAlertTitle.Sorry,
    //                     icon: WarningIcon,
    //                     text: err.message || Message.UnableToReadExcel,
    //                 });
    //             }
    //         };

    //         reader.readAsArrayBuffer(file);
    //     } catch (error: any) {
    //         console.error('Unexpected error:', error);
    //         Swal.fire({
    //             title: SweetAlertTitle.Sorry,
    //             icon: WarningIcon,
    //             text: Message.UnableToReadExcel,
    //         });
    //     }
    // };

    const exportTableToExcel = async () => {
        if (selectedContent) {
            // const data = [excelDataHeadersLabel, ...selectedContent];

            // const ws = XLSX.utils.aoa_to_sheet(data);

            // const wb = XLSX.utils.book_new();
            // XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

            // const excelBase64Data = XLSX.write(wb, { bookType: 'xlsx', type: 'base64' });
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Sheet1');
            worksheet.addRow(excelDataHeadersLabel);
            selectedContent.forEach((row) => worksheet.addRow(row));
            const buffer = await workbook.xlsx.writeBuffer();
            // const base64Data = Buffer.from(buffer).toString('base64');
            const uint8Array = new Uint8Array(buffer);
            const binaryString = String.fromCharCode(...uint8Array);
            const base64Data = btoa(binaryString);

            setExcelDataAsBase64String(base64Data);
        }
    };

    const initialErrors = useInitialErrors(initialValues, getValidationSchema());

    const resetState = () => {
        setHeaders([]);
        setHeaderContent([]);
        setSelectedHeaders(['', '', '']);
        setSelectedContent([]);
        setDisableSelectOption(true);
        setExcelData(null);
        setShowDataPreview(false);
        setSelectedOptionLabels(['']);
    };

    const submitForm = useCallback(
        async (values: ExcelApiFormDataType) => {
            if (hasUser && hasUser.companyId) {
                const requestData: ExcelApiFormDataType = {
                    ...values,
                    companyId: hasUser && hasUser.companyId,
                    isNewCandidateExcelUpload: !isExistingCandidate,
                };

                const file = await getFileFromUrl(excelDataAsBase64String);
                let formData = new FormData();
                formData.append('request', JSON.stringify(requestData));
                file.preview.url && formData.append('excelFile', file);

                try {
                    setDisableBtn(true);
                    let isSuccessfulResponse;
                    if (isExistingCandidate) {
                        const {
                            isSuccessful,
                            resultObject: { duplicateRecords, excelTotalOutputModel, failRecords, passRecords },
                        } = await excelUploadDataService.setCandidateExcelUploadData(formData);

                        isSuccessfulResponse = isSuccessful;
                        setTotalOutPutData(excelTotalOutputModel);
                        setFailedRecords(failRecords);
                        setDuplicateRecords(duplicateRecords);
                        setPassRecords(passRecords);
                    } else {
                        const {
                            isSuccessful,
                            resultObject: { duplicateRecords, excelTotalOutputModel, failRecords, passRecords },
                        } = await excelUploadDataService.setCandidateExcelUploadData(formData);

                        isSuccessfulResponse = isSuccessful;
                        setTotalOutPutData(excelTotalOutputModel);
                        setFailedRecords(failRecords);
                        setDuplicateRecords(duplicateRecords);
                        setPassRecords(passRecords);
                    }

                    if (isSuccessfulResponse) {
                        Swal.fire({
                            icon: SuccessIcon,
                            title: SweetAlertTitle.Status,
                            text: Message.ExcelProcessed,
                            allowOutsideClick: false,
                        }).then((result) => {
                            if (result.isConfirmed) {
                                setOpenProcessedDataModal(true);
                                setDisableBtn(false);
                            }
                        });
                    } else {
                        setDisableBtn(false);
                    }
                } catch (err) {
                    setDisableBtn(false);
                    Swal.fire({
                        icon: ErrorIcon,
                        title: SweetAlertTitle.Error,
                        text: Message.SomethingWentWrong,
                        allowOutsideClick: false,
                    });
                }
            }
        },
        [excelDataAsBase64String, isExistingCandidate]
    );

    useEffect(() => {
        if (selectedContent) {
            exportTableToExcel();
            setFilteredData([...selectedContent]);
        }
    }, [selectedContent]);

    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    useEffect(() => {
        signOutServiceComponent();
    }, [showDataPreview, openProcessedDataModal]);

    useEffect(() => {
        if (!isExistingCandidate) {
            setDummyData([{ Email: '', FirstName: '', MiddleName: '', LastName: '' }]);
            setExcelDataHeadersLabel(['Email', 'First name', 'Middle name', 'Last name']);
        } else {
            setDummyData([{ Email: '', FirstName: '', MiddleName: '', LastName: '', JobTitle: '' }]);
            setExcelDataHeadersLabel(['Email', 'First name', 'Middle name', 'Last name', 'Job title']);
        }
    }, [isExistingCandidate]);

    return (
        <div className="">
            {/* <div className="row my-4">
                <span className="mt-2 ps-4 pe-2 text-secondary">
                    <span
                        onClick={() => {
                            setShowCandidateUploadContent(false);
                        }}
                        className="cursor-pointer"
                    >
                        <i className="bi bi-chevron-left lt-text-secondary-alt me-0"></i>
                    </span>
                    <span className="ms-2 fs-20 fw-600">Candidate data upload</span>
                </span>
            </div> */}

            {isExistingCandidate && (
                <div className="d-flex align-items-center justify-content-center">
                    <div className="mb-3">
                        <div className="alert lt-bg-faq-light-alt alert-dismissible fade show mb-0" role="alert">
                            <span className="fs-12 lt-text-dark-blue">
                                <i className="bi bi-exclamation-triangle"></i>{' '}
                                <span className="fw-600" style={{ letterSpacing: '1px' }}>
                                    Attention:
                                </span>{' '}
                                {Message.ExistingCandiateBulkInvitesMessage}
                            </span>
                            <button
                                type="button"
                                className="btn-close fs-14 lt-text-dark-blue"
                                data-bs-dismiss="alert"
                                aria-label="Close"
                                onClick={() => sessionStorageSetItem('nameCheckAlert', '1')}
                            ></button>
                        </div>
                    </div>
                </div>
            )}
            <div className="container-fluid">
                <Formik
                    onSubmit={submitForm}
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={getValidationSchema()}
                    initialErrors={initialErrors}
                >
                    {(formikProps: FormikProps<any>) => {
                        const { handleSubmit, isSubmitting, setFieldTouched, setFieldValue, resetForm, values } =
                            formikProps;

                        return (
                            <form onSubmit={handleSubmit}>
                                <div className="row d-flex mt-4">
                                    <div className="col-md-3 lt-bg-gray-10 p-3 border-radius-5">
                                        <div className="d-flex">
                                            <div className="me-2 mt-1">
                                                <img loading='lazy'src={excelIcon} alt="Sample excel sheet" />
                                            </div>
                                            <div>
                                                <div>Sample Excel Sheet</div>
                                                <p className="text-secondary fs-10 mb-2">
                                                    {Message.DownloadExcelSample}
                                                </p>
                                                <DataAsExcel
                                                    data={dummyData}
                                                    id="sampleData"
                                                    text="Download"
                                                    FileName={
                                                        isExistingCandidate
                                                            ? "Employee's data upload"
                                                            : "Candidate's data upload"
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 bulk-upload-drop">
                                        <Field name="excelDataFileUrl">
                                            {(fieldProps: FieldProps) => {
                                                const { field } = fieldProps;

                                                return (
                                                    <div>
                                                        <Files
                                                            className={
                                                                !showDataPreview
                                                                    ? 'files-dropzone'
                                                                    : 'files-dropzone disable-dropzone'
                                                            }
                                                            key={resetInputField}
                                                            onChange={(files: ReactFilesFile[]) => {
                                                                handleFileInputChange(files);
                                                                setResetInputField(resetInputField);
                                                                setSelectedContent([]);
                                                                setHeaderContent([]);
                                                                setSelectedOptionLabels([]);
                                                                if (files[0]) {
                                                                    const file = files[0];

                                                                    setFieldTouched(field.name);
                                                                    setFieldValue(field.name, file);
                                                                }
                                                            }}
                                                            onError={(error: ReactFilesError) => {
                                                                if (error.code === 1) {
                                                                }
                                                            }}
                                                            accepts={['.xls', '.xlsx']}
                                                            clickable={showDataPreview ? false : true}
                                                        >
                                                            <div className="drag-drop-box">
                                                                <div className="text-center">
                                                                    <i className="bi bi-upload fs-28" />
                                                                    <p>Drop your file to upload or browse</p>
                                                                    <div className="fs-12 lt-text-disabled mt-2">
                                                                        Supports only XLS & XLSX
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Files>
                                                        <small className="text-danger fs-12 mt-1">
                                                            {excelData?.error}
                                                        </small>
                                                    </div>
                                                );
                                            }}
                                        </Field>
                                    </div>

                                    {excelData?.file && showDataPreview && (
                                        <div className="col-md-3">
                                            <div className="uploaded-div">
                                                <div className="fs-14 mb-2">Uploaded file:</div>
                                                <div className="d-flex align-items-center uploaded-border">
                                                    <img
                                                        src={excelIcon}
                                                        width={20}
                                                        className="me-2"
                                                        alt="Uploaded file"
                                                    />
                                                    <div className="fs-12">{excelData?.file?.name}</div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (excelData?.file) {
                                                                setFieldValue('excelDataFileUrl', '');
                                                                // setExcelData(null);
                                                                resetState();
                                                                setResetInputField(resetInputField + 1);
                                                                setSearchQueries({});
                                                            }
                                                        }}
                                                        title="Delete"
                                                        className="ms-2 lh-1"
                                                    >
                                                        <IconTrash />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* {headers.length > 0 && !showDataPreview ? (
                                        <PreviewExcelData
                                            showDataPreview={!showDataPreview}
                                            setShowDataPreview={setShowDataPreview}
                                            excelDataHeadersLabel={ExcelDataHeadersLabel}
                                            disableSelectOption={disableSelectOption}
                                            headerContent={headerContent}
                                            headers={headers}
                                            selectedHeaders={selectedHeaders}
                                            setSelectedHeaders={setSelectedHeaders}
                                            selectedOptionLabels={selectedOptionLabels}
                                            setSelectedOptionLabels={setSelectedOptionLabels}
                                            setSelectedContent={setSelectedContent}
                                        />
                                    ) : ( */}

                                <>
                                    {' '}
                                    {disableBtn && <RoundTimer duration={180} />}
                                    <div className="container-fluid mt-2 ps--0">
                                        <div className="row">
                                            <div className="col-md-12 ps-0">
                                                <div className="table-responsive excel-table-height">
                                                    <table className="table">
                                                        <thead>
                                                            <tr>
                                                                {excelDataHeadersLabel.map((header, index) => (
                                                                    <>
                                                                        <th key={index}>
                                                                            {header && (
                                                                                <div
                                                                                    className="mb-2"
                                                                                    onClick={() =>
                                                                                        handleSorting(header)
                                                                                    }
                                                                                    style={{
                                                                                        cursor: 'pointer',
                                                                                        marginLeft: '5px',
                                                                                    }}
                                                                                >
                                                                                    <div key={header}>
                                                                                        {header}{' '}
                                                                                        <span>
                                                                                            {sortingOrder[header] ===
                                                                                            'asc' ? (
                                                                                                <i className="bi bi-chevron-up"></i>
                                                                                            ) : (
                                                                                                <i className="bi bi-chevron-down"></i>
                                                                                            )}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                placeholder={`Search ${header}...`}
                                                                                value={searchQueries[header] || ''}
                                                                                onChange={(e) =>
                                                                                    updateSearchQuery(
                                                                                        header,
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            />
                                                                        </th>
                                                                    </>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        {showDataPreview && selectedContent.length > 0 ? (
                                                            <tbody>
                                                                {filteredData.map((row, rowIndex) => (
                                                                    <tr key={rowIndex}>
                                                                        {row.map((cell, cellIndex) => (
                                                                            <td key={cellIndex}>{cell}</td>
                                                                        ))}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        ) : (
                                                            <tbody>
                                                                <tr>
                                                                    <td colSpan={5}>
                                                                        <div className="offer-container">
                                                                            <img
                                                                                src={imgNoResult}
                                                                                alt=""
                                                                                className="offer-no-result-img"
                                                                            />
                                                                            <div className="mt-2">No results</div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        )}
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {showDataPreview && selectedContent.length > 0 && (
                                        <div className="text-end me-3 mt-3">
                                            <button
                                                type="submit"
                                                disabled={disableBtn}
                                                className="mt-3 btn btn-primary"
                                            >
                                                {disableBtn && (
                                                    <span className="microsoft-icon">
                                                        <span className="spinner-border spinner-border-sm" />
                                                    </span>
                                                )}
                                                Process
                                            </button>

                                            {/* <button
                                                        type="button"
                                                        onClick={() => {
                                                            exportTableToExcel2();
                                                        }}
                                                    >
                                                        Download
                                                    </button> */}
                                        </div>
                                    )}
                                </>

                                {openProcessedDataModal && (
                                    <DownloadExcelResponse
                                        resetState={resetState}
                                        setFieldValue={setFieldValue}
                                        openProcessedDataModal={openProcessedDataModal}
                                        setOpenProcessedDataModal={setOpenProcessedDataModal}
                                        totalOutPutData={totalOutPutData}
                                        duplicateRecords={duplicateRecords}
                                        failedRecords={failedRecords}
                                        passRecords={passRecords}
                                        setIsLead={setIsLead}
                                        setShowCandidateUploadContent={setShowCandidateUploadContent}
                                    />
                                )}
                            </form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
};

export default CandidateExcelBulkUpload;

const getValidationSchema = () =>
    objectYup().shape({
        excelDataFileUrl: mixedYup().required('Required field.'),
    });

// async function getFileFromUrl(base64Object: string) {
//     const name = 'excelUploadData.xlsx';

//     function convertToByteArray(base64: string): Uint8Array {
//         const byteCharacters = atob(base64);
//         const byteNumbers = new Array(byteCharacters.length);
//         for (let i = 0; i < byteCharacters.length; i++) {
//             byteNumbers[i] = byteCharacters.charCodeAt(i);
//         }
//         return new Uint8Array(byteNumbers);
//     }

//     const file = new File([convertToByteArray(base64Object)], name, {
//         type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
//     });

//     const reactFile = file as ReactFilesFile;
//     reactFile.preview = {
//         type: 'file',
//         url: URL.createObjectURL(file),
//     };
//     reactFile.id = name;
//     reactFile.extension = 'xlsx';

//     return reactFile;
// }

async function getFileFromUrl(base64Object: string) {
    const name = 'excelUploadData.xlsx';

    const binaryString = atob(base64Object);
    const buffer = new ArrayBuffer(binaryString.length);
    const view = new Uint8Array(buffer);

    for (let i = 0; i < binaryString.length; i++) {
        view[i] = binaryString.charCodeAt(i);
    }

    const file = new File([buffer], name, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const reactFile = file as ReactFilesFile;
    reactFile.preview = {
        type: 'file',
        url: URL.createObjectURL(file),
    };
    reactFile.id = name;
    reactFile.extension = 'xlsx';

    return reactFile;
}
